import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  Focusable,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon as LaunchpadIcon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { observableToPromise } from '../../../shared/observableToPromise';
import { type ActionAndDescription, getActions } from '../../api';
import { type Account } from '../../model';

// Vital's resource-type values are resource-specifier prefixes (e.g. `proj`,
// `env`, `acct`), but the actions map is keyed by resource-kind names
// (`project`, `environment`, `account`). Map the prefixes that differ; anything
// not listed already matches its action-map key (e.g. `flag`, `segment`).
const RESOURCE_TYPE_TO_ACTION_KEY: Record<string, string> = {
  proj: 'project',
  env: 'environment',
  acct: 'account',
};

type ActionFilterProps = Readonly<{
  account: Account;
  // Undefined when no resource is selected; the filter is then disabled.
  resourceType?: string;
  resourceLabel?: string;
  isDisabled: boolean;
  selectedActions: readonly string[];
  onChange: (actions: string[]) => void;
}>;

export function ActionFilter({
  account,
  resourceType,
  resourceLabel,
  isDisabled,
  selectedActions,
  onChange,
}: ActionFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['vital-actions', account._id],
    queryFn: ({ signal }) => observableToPromise(getActions(account), signal),
    enabled: !isDisabled,
    staleTime: Infinity,
  });

  const actions = useMemo<ActionAndDescription[]>(() => {
    if (!resourceType) {
      return [];
    }
    const actionKey = RESOURCE_TYPE_TO_ACTION_KEY[resourceType] ?? resourceType;
    const list = data?.[actionKey] ?? [];
    return [...list]
      .filter((item) => Boolean(item.action))
      .sort((a, b) => (a.action ?? '').localeCompare(b.action ?? ''));
  }, [data, resourceType]);

  const filteredActions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return actions;
    }
    return actions.filter(
      (item) =>
        (item.action ?? '').toLowerCase().includes(query) || (item.description ?? '').toLowerCase().includes(query),
    );
  }, [actions, search]);

  const selectedCount = selectedActions.length;
  let valueLabel = 'any';
  if (selectedCount === 1) {
    [valueLabel] = selectedActions;
  } else if (selectedCount > 1) {
    valueLabel = `${selectedCount} actions`;
  }

  if (isDisabled) {
    return (
      <TooltipTrigger>
        <Focusable>
          <span
            className="AuditLogEntry-simpleToolbarControl AuditLogEntry-simpleToolbarControl--action AuditLogEntry-simpleToolbarControl--disabled"
            role="button"
            aria-disabled
            aria-label="Action filter (select a resource first)"
            tabIndex={0}
          >
            <span className="AuditLogEntry-simpleToolbarFilterIconWrap">
              <LaunchpadIcon name="filter-list" size="small" />
            </span>
            <span className="AuditLogEntry-simpleToolbarControlLabel">Action</span>
            <span className="AuditLogEntry-simpleToolbarAnyValue">any</span>
            <LaunchpadIcon className="AuditLogEntry-simpleToolbarControlEndIcon" name="chevron-down" />
          </span>
        </Focusable>
        <Tooltip placement="bottom" variant="popover">
          Select a resource type first to filter by action.
        </Tooltip>
      </TooltipTrigger>
    );
  }

  return (
    <DialogTrigger>
      <Button
        aria-label="Action filter"
        className="AuditLogEntry-simpleToolbarControl AuditLogEntry-simpleToolbarControl--action"
        onPress={() => {
          setIsOpen(true);
          setSearch('');
        }}
      >
        <span className="AuditLogEntry-simpleToolbarFilterIconWrap">
          <LaunchpadIcon name="filter-list" size="small" />
          {selectedCount > 0 && <span className="AuditLogEntry-simpleToolbarFilterDot" />}
        </span>
        <span className="AuditLogEntry-simpleToolbarControlLabel">Action</span>
        {selectedCount > 0 ? (
          <span className="AuditLogEntry-simpleToolbarSelectedValueText">{valueLabel}</span>
        ) : (
          <span className="AuditLogEntry-simpleToolbarAnyValue">any</span>
        )}
        <LaunchpadIcon className="AuditLogEntry-simpleToolbarControlEndIcon" name="chevron-down" slot="end-icon" />
      </Button>
      <Popover
        placement="bottom start"
        isOpen={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          setSearch('');
        }}
      >
        <Dialog aria-label={`Filter by ${resourceLabel ?? 'resource'} action`}>
          <div className="AuditLogEntry-memberSearchContainer AuditLogEntry-actionFilterContainer">
            <div className="AuditLogEntry-actionFilterHeader">
              <span className="AuditLogEntry-actionFilterTitle">Filter {resourceLabel ?? 'resource'} actions</span>
              {selectedCount > 0 && (
                <Button variant="minimal" size="small" onPress={() => onChange([])}>
                  Clear
                </Button>
              )}
            </div>
            <div className="AuditLogEntry-memberSearchWithClear">
              <SearchField aria-label="Search actions" autoFocus>
                <Group>
                  <LaunchpadIcon name="search" size="small" />
                  <Input
                    placeholder="Search actions..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </Group>
              </SearchField>
            </div>
            <ListBox
              selectionMode="multiple"
              aria-label="Action options"
              selectedKeys={new Set(selectedActions)}
              renderEmptyState={() => (
                <div className="AuditLogEntry-memberNoResults">
                  {isLoading ? 'Loading actions...' : 'No actions found'}
                </div>
              )}
              onSelectionChange={(keys) => {
                if (keys === 'all') {
                  onChange(actions.map((item) => item.action ?? '').filter(Boolean));
                  return;
                }
                onChange(Array.from(keys as Set<string>));
              }}
            >
              {filteredActions.map((item) => (
                <ListBoxItem key={item.action} id={item.action} textValue={item.action}>
                  <Text slot="label">{item.action}</Text>
                  {item.description && (
                    <Text slot="description" maxLines={2}>
                      {item.description}
                    </Text>
                  )}
                </ListBoxItem>
              ))}
            </ListBox>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
